import {
    type InfiniteData,
    QueryFunction,
    useInfiniteQuery,
    useMutation,
    useQuery,
} from "@tanstack/react-query";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import { extractApiErrorMessage, getApiErrorMessage } from "./apiError";
import {
    ClientInferResponseBody,
    ServerInferResponseBody,
} from "@ts-rest/core";
import {
    invoiceContract,
    InvoiceFilters,
    InvoiceQueryParams,
    InvoiceStatsParams,
    InvoicesByCounterpartyQueryParams,
} from "@common/contracts/invoice.contract";
import {
    parseDateOnlyAsUTC,
    withCanonicalDateFilters,
} from "@common/helpers/date";
import { invoicesPageDtoSchema } from "@common/schemas/domains/invoice/invoice-response.schema";
import { PatchImporterInvoiceItem } from "@common/schemas/domains/invoice/patch-importer-invoices.schema";
import { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import {
    Invoice,
    InvoiceBase,
    InvoiceSource,
    InvoiceStatus,
} from "@common/types/invoice";
import { ResourceType } from "@common/types/auditLog";
import {
    AccrualMatchedTransactionDisplay,
    InvoiceAccrualMatchSummary,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { ImportedSettlementDisplay } from "@common/schemas/domains/accrualReview/imported-settlement-display.schema";
import { SuggestedAccrualMatchTransactionsForInvoiceQuery } from "@common/schemas/domains/accrualReview/suggested-accrual-match-transactions-for-invoice.schema";
import {
    GetTransactionsResponseWithCount,
    getTransactionsResponseWithCountSchema,
} from "@common/schemas/transaction.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { InvoiceScheduleChange } from "@common/schemas/domains/invoice/invoice-schedule-change.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { queryClient } from "../queryClient";
import { auditLogQueryKeys } from "./auditLog.api";
import { invalidateHasMatchableDocumentsQuery } from "./accrual-review.api";

export const INVOICE_ACCRUAL_MATCHES_QUERY_KEY = "invoice-accrual-matches-v2";
export const INVOICE_IMPORTED_SETTLEMENTS_QUERY_KEY =
    "invoice-imported-settlements";

export interface PatchImporterInvoicesResponse {
    data: InvoiceBase[];
    scheduleChanges: InvoiceScheduleChange[];
}

const invoiceContractClient = getBackendAPIClient(invoiceContract, undefined, {
    jsonQuery: true,
});

type InvoiceQueryKey = [
    "invoices",
    string | undefined,
    InvoiceQueryParams | undefined,
];
type InvoiceStatsQueryKey = [
    "invoiceStats",
    string | undefined,
    InvoiceStatsParams | undefined,
];
type InvoiceListStatisticsQueryKey = [
    "invoiceListStatistics",
    string | undefined,
    InvoiceStatsParams | undefined,
];

type InvoicesByCounterpartyQueryKey = [
    "invoicesByCounterparty",
    string | undefined,
    InvoicesByCounterpartyQueryParams | undefined,
];

type GetInvoicesResponse = ClientInferResponseBody<
    typeof invoiceContract.getInvoices,
    200
>;
type GetInvoiceStatsResponse = ServerInferResponseBody<
    typeof invoiceContract.getInvoiceStats,
    200
>;
type GetInvoiceListStatisticsResponse = ServerInferResponseBody<
    typeof invoiceContract.getInvoiceListStatistics,
    200
>;
type GetInvoicesByCounterpartyResponse = ServerInferResponseBody<
    typeof invoiceContract.getInvoicesByCounterparty,
    200
>;

export const getInvoicesQueryKey = (
    workspaceId?: string,
    params?: InvoiceQueryParams,
): InvoiceQueryKey => ["invoices", workspaceId, params];

const getInvoiceStatsQueryKey = (
    workspaceId?: string,
    params?: InvoiceStatsParams,
): InvoiceStatsQueryKey => ["invoiceStats", workspaceId, params];

const getInvoiceListStatisticsQueryKey = (
    workspaceId?: string,
    params?: InvoiceStatsParams,
): InvoiceListStatisticsQueryKey => [
    "invoiceListStatistics",
    workspaceId,
    params,
];

const getInvoicesByCounterpartyQueryKey = (
    workspaceId?: string,
    params?: InvoicesByCounterpartyQueryParams,
): InvoicesByCounterpartyQueryKey => [
    "invoicesByCounterparty",
    workspaceId,
    params,
];

export function useInvoices(
    query: InvoiceQueryParams,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const activeWorkspace = useActiveWorkspace();
    const queryKey = getInvoicesQueryKey(activeWorkspace?.id, query);

    return useQuery({
        queryKey,
        queryFn: fetchInvoices,
        enabled,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

// The workspace-records probe only checks whether any records exist at all,
// which changes rarely; a short stale window keeps it from re-firing (and
// flashing the empty state) as the user edits search/filters.
const WORKSPACE_RECORDS_PROBE_STALE_TIME = 60_000;

export function useHasInvoicesInWorkspace(enabled: boolean) {
    const activeWorkspace = useActiveWorkspace();

    const { data, isLoading } = useQuery({
        queryKey: getInvoicesQueryKey(activeWorkspace?.id, {
            page: 1,
            limit: 1,
        }),
        queryFn: fetchInvoices,
        enabled,
        staleTime: WORKSPACE_RECORDS_PROBE_STALE_TIME,
    });

    return {
        hasRecords: (data?.total ?? 0) > 0,
        isChecking: enabled && isLoading,
    };
}

export function useHasInvoiceCounterpartiesInWorkspace(enabled: boolean) {
    const activeWorkspace = useActiveWorkspace();

    const { data, isLoading } = useQuery({
        queryKey: getInvoicesByCounterpartyQueryKey(activeWorkspace?.id, {
            page: 1,
            limit: 1,
        }),
        queryFn: fetchInvoicesByCounterparty,
        enabled,
        staleTime: WORKSPACE_RECORDS_PROBE_STALE_TIME,
    });

    return {
        hasRecords: (data?.total ?? 0) > 0,
        isChecking: enabled && isLoading,
    };
}

type LastInvoiceNumberQueryKey = [
    "lastInvoiceNumber",
    string | undefined,
    number | undefined,
];

const getLastInvoiceNumberQueryKey = (
    workspaceId?: string,
    entityId?: number,
): LastInvoiceNumberQueryKey => ["lastInvoiceNumber", workspaceId, entityId];

const fetchLastInvoiceNumber: QueryFunction<
    string | null,
    LastInvoiceNumberQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, entityId] = queryKey;
    if (!workspaceId) {
        return null;
    }
    const result = await invoiceContractClient.getLastInvoiceNumber({
        params: { workspaceId },
        query: { entityId },
    });
    return result.body.invoiceNumber;
};

export function useLastInvoiceNumber({
    enabled = true,
    entityId,
}: { enabled?: boolean; entityId?: number } = {}) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: getLastInvoiceNumberQueryKey(
            workspaceId ?? undefined,
            entityId,
        ),
        queryFn: fetchLastInvoiceNumber,
        enabled: enabled && Boolean(workspaceId),
        staleTime: 0,
    });
}

export function useInvoiceStats(query: InvoiceStatsParams) {
    const activeWorkspace = useActiveWorkspace();

    return useQuery({
        queryKey: getInvoiceStatsQueryKey(activeWorkspace?.id, query),
        queryFn: fetchInvoiceStats,
    });
}

// Backs the invoices table command bar total/average/count display. Mirrors
// useTransactionsStatisticsQuery: the frontend layers selected-rows scoping on
// top of this all-filtered-rows aggregate the same way
// useFooterPassingStatistics does for transactions.
export function useInvoiceListStatistics(
    query: InvoiceStatsParams,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const activeWorkspace = useActiveWorkspace();

    const result = useQuery({
        queryKey: getInvoiceListStatisticsQueryKey(activeWorkspace?.id, query),
        queryFn: fetchInvoiceListStatistics,
        enabled: enabled && !!activeWorkspace?.id,
    });

    return { ...result, data: result.data?.data };
}

export function useInvoicesByCounterparty(
    query: InvoicesByCounterpartyQueryParams,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const activeWorkspace = useActiveWorkspace();
    const queryKey = getInvoicesByCounterpartyQueryKey(
        activeWorkspace?.id,
        query,
    );

    return useQuery({
        queryKey,
        queryFn: fetchInvoicesByCounterparty,
        enabled,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

const INVOICE_PICKER_PAGE_SIZE = 50;

type InvoicePickerQuery = Omit<InvoiceQueryParams, "page" | "limit">;

/**
 * Pages of invoices for a picker, 50 at a time, flattened into one list.
 * Lives under the `invoices` key prefix so invoice invalidations refresh it.
 */
export function useInvoicePickerQuery(
    query: InvoicePickerQuery,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const workspaceId = useActiveWorkspace()?.id;

    return useInfiniteQuery({
        queryKey: ["invoices", workspaceId, "picker", query],
        queryFn: async ({ pageParam }) => {
            if (!workspaceId) {
                return { total: 0, pageCount: 0, data: [] };
            }
            return await loadInvoicesPage(workspaceId, {
                ...query,
                page: pageParam,
                limit: INVOICE_PICKER_PAGE_SIZE,
            });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, _pages, lastPageNumber) =>
            lastPageNumber < lastPage.pageCount
                ? lastPageNumber + 1
                : undefined,
        select: flattenInvoicePages,
        enabled: enabled && Boolean(workspaceId),
    });
}

// Page order can shift between requests (a new invoice lands on page 1), so an
// invoice may come back on two pages; keep its first appearance.
function flattenInvoicePages(
    data: InfiniteData<{ data: Invoice[] }, number>,
): Invoice[] {
    const invoicesById = new Map<string, Invoice>();
    for (const page of data.pages) {
        for (const invoice of page.data) {
            if (!invoicesById.has(invoice.id)) {
                invoicesById.set(invoice.id, invoice);
            }
        }
    }
    return [...invoicesById.values()];
}

/**
 * One page of invoices; throws when the request fails. `page` is 1-based
 * here; the endpoint takes it 0-based.
 */
export async function loadInvoicesPage(
    workspaceId: string,
    query: InvoiceQueryParams,
) {
    const result = await invoiceContractClient.getInvoices({
        params: { workspaceId },
        query: {
            ...query,
            filters: query.filters
                ? withCanonicalDateFilters(query.filters)
                : query.filters,
            page: query.page - 1,
            limit: query.limit,
        },
    });
    if (result.status !== 200) {
        throw new Error("Failed to load invoices");
    }
    return invoicesPageDtoSchema.parse(result.body);
}

const fetchInvoices: QueryFunction<
    GetInvoicesResponse | undefined,
    InvoiceQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId || !query) {
        return;
    }
    return await loadInvoicesPage(workspaceId, query);
};

const fetchInvoiceStats: QueryFunction<
    GetInvoiceStatsResponse | undefined,
    InvoiceStatsQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId) {
        return;
    }
    const result = await invoiceContractClient.getInvoiceStats({
        params: { workspaceId },
        query: {
            ...query,
            filters: query?.filters
                ? withCanonicalDateFilters(query.filters)
                : query?.filters,
        },
    });
    return result.body;
};

const fetchInvoiceListStatistics: QueryFunction<
    GetInvoiceListStatisticsResponse | undefined,
    InvoiceListStatisticsQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId) {
        return;
    }
    const result = await invoiceContractClient.getInvoiceListStatistics({
        params: { workspaceId },
        query: {
            ...query,
            filters: query?.filters
                ? withCanonicalDateFilters(query.filters)
                : query?.filters,
        },
    });
    return result.body;
};

const fetchInvoicesByCounterparty: QueryFunction<
    GetInvoicesByCounterpartyResponse | undefined,
    InvoicesByCounterpartyQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId) {
        return;
    }
    const result = await invoiceContractClient.getInvoicesByCounterparty({
        params: { workspaceId },
        query: {
            ...query,
            filters: query?.filters
                ? withCanonicalDateFilters(query.filters)
                : query?.filters,
            page: (query?.page ?? 1) - 1,
            limit: query?.limit ?? 10,
        },
    });
    return result.body;
};

export function useCreateInvoices() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoices,
        }: {
            workspaceId: string;
            invoices: Parameters<
                typeof invoiceContractClient.createInvoices
            >[0]["body"]["invoices"];
        }) => {
            const result = await invoiceContractClient.createInvoices({
                params: { workspaceId },
                body: { invoices },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
    });
}

/**
 * Error toasts are left to the caller: a save that would rewrite a locked
 * schedule month answers with a structured 409 that has its own dialog.
 */
export function usePatchImporterInvoices(
    options: {
        onSuccess?: (response: PatchImporterInvoicesResponse) => void;
    } = {},
) {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            idsToRemove,
            upserts,
        }: {
            workspaceId: string;
            idsToRemove: string[];
            upserts: PatchImporterInvoiceItem[];
        }): Promise<PatchImporterInvoicesResponse> => {
            const result = await invoiceContractClient.patchImporterInvoices({
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                params: { workspaceId },
                body: { idsToRemove, upserts },
            });
            if (result.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        result.body,
                        "Failed to update invoices",
                    ),
                );
            }
            return result.body;
        },
        onSuccess: (response) => {
            invalidateInvoiceQueries();
            options.onSuccess?.(response);
        },
    });
}

export function findInvoiceInListCache(invoiceId: string): Invoice | undefined {
    const cachedQueries = queryClient.getQueriesData<{
        data?: Invoice[];
    }>({ queryKey: ["invoices"], type: "active" });
    for (const [, response] of cachedQueries) {
        const match = response?.data?.find(
            (invoice) => invoice.id === invoiceId,
        );
        if (match) {
            return match;
        }
    }
    return undefined;
}

export async function invalidateInvoiceQueries(): Promise<void> {
    invalidateHasMatchableDocumentsQuery();
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: ["invoices"],
        }),
        queryClient.invalidateQueries({
            queryKey: ["invoiceStats"],
        }),
        queryClient.invalidateQueries({
            queryKey: ["invoicesByCounterparty"],
        }),
        queryClient.invalidateQueries({
            queryKey: ["lastInvoiceNumber"],
        }),
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey;
                if (key[0] !== auditLogQueryKeys.all[0]) {
                    return false;
                }
                if (key[1] === "byResource") {
                    return key[3] === ResourceType.INVOICE;
                }
                return key[1] === "history" || key[1] === "history-details";
            },
        }),
    ]);
}

export function useUpdateInvoiceLineItem() {
    return useMutation({
        mutationFn: ({
            workspaceId,
            lineItemId,
            classIds,
            accountOverrides,
            accountsReceivableOverrides,
            accountId,
        }: {
            workspaceId: string;
            lineItemId: string;
            classIds?: string[];
            accountOverrides?: AccountOverrideEntryRequest[];
            accountsReceivableOverrides?: AccountOverrideEntryRequest[];
            accountId?: string | null;
        }) =>
            updateInvoiceLineItem({
                workspaceId,
                lineItemId,
                classIds,
                accountOverrides,
                accountsReceivableOverrides,
                accountId,
            }),
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
    });
}

export function useBulkUpdateInvoiceLineItems() {
    return useMutation({
        mutationFn: ({
            workspaceId,
            classIds,
            accountOverrides,
            accountsReceivableOverrides,
            lineItemIds,
            filters,
        }: {
            workspaceId: string;
            classIds?: string[];
            accountOverrides?: AccountOverrideEntryRequest[];
            accountsReceivableOverrides?: AccountOverrideEntryRequest[];
            lineItemIds?: string[];
            filters?: InvoiceFilters;
        }) =>
            bulkUpdateInvoiceLineItems({
                workspaceId,
                classIds,
                accountOverrides,
                accountsReceivableOverrides,
                lineItemIds,
                filters,
            }),
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
    });
}

async function updateInvoiceLineItem({
    workspaceId,
    lineItemId,
    classIds,
    accountOverrides,
    accountsReceivableOverrides,
    accountId,
}: {
    workspaceId: string;
    lineItemId: string;
    classIds?: string[];
    accountOverrides?: AccountOverrideEntryRequest[];
    accountsReceivableOverrides?: AccountOverrideEntryRequest[];
    accountId?: string | null;
}) {
    const result = await invoiceContractClient.updateInvoiceLineItem({
        params: { workspaceId, lineItemId },
        body: {
            classIds,
            accountOverrides,
            accountsReceivableOverrides,
            accountId,
        },
    });

    return result.body;
}

export function useUpdateInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceId,
            uncollectableAt,
            voidedAt,
            paidAt,
            due,
            ...body
        }: {
            workspaceId: string;
            invoiceId: string;
            memo?: string | null;
            status?: InvoiceStatus;
            uncollectableAt?: string | null;
            voidedAt?: string | null;
            paidAt?: string | null;
            due?: string | null;
            // Authorizes voiding a matched Open invoice as part of a revision
            // flow (only valid with { status: VOID, voidedAt }). See §5.3.
            revisionInvoiceId?: string;
        }) => {
            const result = await invoiceContractClient.updateInvoice({
                params: { workspaceId, invoiceId },
                body: {
                    ...body,
                    ...(uncollectableAt !== undefined && { uncollectableAt }),
                    ...(voidedAt !== undefined && { voidedAt }),
                    ...(paidAt !== undefined && {
                        paidAt: paidAt ? new Date(paidAt) : null,
                    }),
                    ...(due !== undefined && {
                        // `due` is still a timestamptz write; UTC midnight
                        // keeps the calendar day stable east of UTC.
                        due: due ? parseDateOnlyAsUTC(due) : null,
                    }),
                },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
        onError: () => {
            invalidateInvoiceQueries();
        },
    });
}

export function useBulkUpdateInvoiceStatus() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceIds,
            status,
            date,
        }: {
            workspaceId: string;
            invoiceIds: string[];
            status: InvoiceStatus.VOID | InvoiceStatus.UNCOLLECTABLE;
            date: string;
        }) => {
            const result = await invoiceContractClient.bulkUpdateInvoiceStatus({
                params: { workspaceId },
                body: { invoiceIds, status, date },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
        onError: () => {
            invalidateInvoiceQueries();
        },
    });
}

export function useBulkUpdateInvoiceCounterparty() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceIds,
            counterpartyId,
        }: {
            workspaceId: string;
            invoiceIds: string[];
            counterpartyId: string | null;
        }) => {
            const result =
                await invoiceContractClient.bulkUpdateInvoiceCounterparty({
                    params: { workspaceId },
                    body: { invoiceIds, counterpartyId },
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
        onError: () => {
            invalidateInvoiceQueries();
        },
    });
}

export function useBulkDownloadInvoicePdf() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceIds,
        }: {
            workspaceId: string;
            invoiceIds: string[];
        }) => {
            const result = await invoiceContractClient.bulkDownloadInvoicePdf({
                params: { workspaceId },
                body: { invoiceIds },
            });
            return result.body;
        },
    });
}

export function useDeleteInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceId,
        }: {
            workspaceId: string;
            invoiceId: string;
        }) => {
            const result = await invoiceContractClient.deleteInvoice({
                params: { workspaceId, invoiceId },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
    });
}

export interface BulkDeleteInvoicesResult {
    succeeded: string[];
    failed: Array<{ invoiceId: string; message: string }>;
}

export function useBulkDeleteInvoices() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceIds,
        }: {
            workspaceId: string;
            invoiceIds: string[];
        }): Promise<BulkDeleteInvoicesResult> => {
            const result: BulkDeleteInvoicesResult = {
                succeeded: [],
                failed: [],
            };
            for (const invoiceId of invoiceIds) {
                try {
                    const response = await invoiceContractClient.deleteInvoice({
                        extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                        params: { workspaceId, invoiceId },
                    });
                    if (response.status === 200) {
                        result.succeeded.push(invoiceId);
                        continue;
                    }
                    result.failed.push({
                        invoiceId,
                        message: extractApiErrorMessage(
                            response.body,
                            "Failed to delete invoice",
                        ),
                    });
                } catch (error) {
                    result.failed.push({
                        invoiceId,
                        message: getApiErrorMessage(
                            error,
                            "Failed to delete invoice",
                        ),
                    });
                }
            }
            return result;
        },
        onSettled: () => {
            invalidateInvoiceQueries();
        },
    });
}

export async function getInvoiceAccrualMatches({
    workspaceId,
    invoiceId,
}: {
    workspaceId: string;
    invoiceId: string;
}): Promise<AccrualMatchedTransactionDisplay[]> {
    const result = await invoiceContractClient.getInvoiceAccrualMatches({
        params: { workspaceId, invoiceId },
    });
    if (result.status !== 200) {
        return [];
    }
    return result.body.matches;
}

export function useInvoiceAccrualMatches(invoiceId: string | undefined) {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: [INVOICE_ACCRUAL_MATCHES_QUERY_KEY, workspaceId, invoiceId],
        queryFn: () =>
            getInvoiceAccrualMatches({
                workspaceId: workspaceId!,
                invoiceId: invoiceId!,
            }),
        enabled: Boolean(workspaceId && invoiceId),
    });
}

async function getInvoiceAccrualMatchSummaries({
    workspaceId,
    invoiceIds,
}: {
    workspaceId: string;
    invoiceIds: string[];
}): Promise<InvoiceAccrualMatchSummary[]> {
    const result = await invoiceContractClient.getInvoiceAccrualMatchSummaries({
        params: { workspaceId },
        query: { invoiceIds },
    });
    if (result.status !== 200) {
        throw new Error("Failed to load invoice matches");
    }
    return result.body.summaries;
}

export function useInvoiceAccrualMatchSummaries({
    invoiceIds,
    enabled,
}: {
    invoiceIds: string[];
    enabled: boolean;
}) {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: [
            INVOICE_ACCRUAL_MATCHES_QUERY_KEY,
            workspaceId,
            "summaries",
            invoiceIds,
        ],
        queryFn: () =>
            getInvoiceAccrualMatchSummaries({
                workspaceId: workspaceId!,
                invoiceIds,
            }),
        enabled: enabled && Boolean(workspaceId) && invoiceIds.length > 0,
    });
}

export async function getSuggestedAccrualMatchTransactionsForInvoice({
    workspaceId,
    invoiceId,
    query,
}: {
    workspaceId: string;
    invoiceId: string;
    query: SuggestedAccrualMatchTransactionsForInvoiceQuery;
}): Promise<GetTransactionsResponseWithCount> {
    const result =
        await invoiceContractClient.getSuggestedAccrualMatchTransactions({
            params: { workspaceId, invoiceId },
            query,
        });
    if (result.status !== 200) {
        throw new Error("Failed to fetch suggested transactions");
    }
    return getTransactionsResponseWithCountSchema.parse(result.body);
}

async function getInvoiceImportedSettlements({
    workspaceId,
    invoiceId,
}: {
    workspaceId: string;
    invoiceId: string;
}): Promise<ImportedSettlementDisplay[]> {
    const result = await invoiceContractClient.getInvoiceImportedSettlements({
        params: { workspaceId, invoiceId },
    });
    if (result.status !== 200) {
        return [];
    }
    return result.body.settlements;
}

export function useInvoiceImportedSettlements({
    invoiceId,
    source,
}: {
    invoiceId: string | undefined;
    source: InvoiceSource | undefined;
}) {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: [
            INVOICE_IMPORTED_SETTLEMENTS_QUERY_KEY,
            workspaceId,
            invoiceId,
        ],
        queryFn: () =>
            getInvoiceImportedSettlements({
                workspaceId: workspaceId!,
                invoiceId: invoiceId!,
            }),
        enabled: Boolean(
            workspaceId && invoiceId && source === InvoiceSource.QUICKBOOKS,
        ),
    });
}

export function useLinkDocumentsToInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceId,
            financialDocumentIds,
        }: {
            workspaceId: string;
            invoiceId: string;
            financialDocumentIds: number[];
        }) => {
            const result = await invoiceContractClient.linkDocumentToInvoice({
                params: { workspaceId, invoiceId },
                body: { financialDocumentIds },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
    });
}

export function useUnlinkDocumentsFromInvoice() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceId,
            financialDocumentIds,
        }: {
            workspaceId: string;
            invoiceId: string;
            financialDocumentIds: number[];
        }) => {
            const result =
                await invoiceContractClient.unlinkDocumentFromInvoice({
                    params: { workspaceId, invoiceId },
                    body: { financialDocumentIds },
                });
            return result.body;
        },
        onSuccess: () => {
            invalidateInvoiceQueries();
        },
    });
}

async function bulkUpdateInvoiceLineItems({
    workspaceId,
    classIds,
    accountOverrides,
    accountsReceivableOverrides,
    lineItemIds,
    filters,
}: {
    workspaceId: string;
    classIds?: string[];
    accountOverrides?: AccountOverrideEntryRequest[];
    accountsReceivableOverrides?: AccountOverrideEntryRequest[];
    lineItemIds?: string[];
    filters?: InvoiceFilters;
}) {
    const result = await invoiceContractClient.bulkUpdateInvoiceLineItems({
        params: { workspaceId },
        body: {
            classIds,
            accountOverrides,
            accountsReceivableOverrides,
            lineItemIds,
            filters,
        },
    });

    return result.body;
}
